/* eslint-disable @typescript-eslint/no-unused-vars */
import Select from '@gerdau/hefesto/Select/Select';
import { Button as HefestoButton, Icon } from '@gerdau/hefesto';
import { Button, Form, Input } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterInputContainer } from '../../../../../components/Commons/Filters/styles';
import { ApplicationState } from '../../../../../store';
import {
  loadCustomerBlocks,
  loadCustomerFromId,
  loadCustomerPurchaseReceivers,
  loadCustomersProductDescriptionV2,
  loadCustomersSuccess
} from '../../../../../store/ducks/customers/actions';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { ILocality2 } from '../../../../../store/commomTypes';

import { updateLongSteelShoppingCartSettings } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { uppercaseOnlyFirstLetter } from '../../../../../utils/formatStrings';
import AdminBpInput from '../../../../../components/AdminBpInput';
import CustomerBlockModalContent from './CustomerBlockModalContent';
import {
  AdminSearchRow,
  Box,
  Container,
  EmitterSelectContainer,
  HeaderErrorContainer,
  HeaderErrorText
} from './styles';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import StepByStepComponent from '../../PurchaseSubHeaderMvp/StepByStepComponent';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { filterInvalidCustomers } from '../../../../../utils/commerceUser';
import { useLocation } from 'react-router-dom';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { useSelectEmitter } from '../../../../../services/MVP/useSelectEmitter';
import { ChangeBPModal } from '../SearchItem/ChangeBPModal';
import { BpUnavailableModal } from '../../../../../components/BpUnavailableModal';
import BpRichDisplay from '../../../../../components/BpRichDisplay';
import { createGlobalStyle } from 'styled-components';
import { GetFlag } from '../../../../../utils/featureFlag';
import { useCartService } from '../../../../../services/MVP/useCartService';
import { useCart } from '../../ShoppingCartMVP/useCart';
import {
  EntryOrderService,
  IShipToCustomerItem
} from '../../../../../services/EntryOrder';
import {
  loadReceiversForBP,
  loadReceiversFrom
} from '../../../../../store/ducks/entry-order/actions';
import { trackPurchaseAttempt } from '../../../../../services/MVP/purchaseAttemptService';

const buildBpAddressString = (locality: ILocality2): string => {
  const { address, city, state, zipCode } = locality;
  const cityState = city && state ? `${city} - ${state}` : city || state || '';
  return [address, cityState, zipCode ? `CEP ${zipCode}` : '']
    .filter(Boolean)
    .join(' | ');
};

const buildBpAddressStringFromReceiver = (r: IShipToCustomerItem): string => {
  const cityState =
    r.city && r.state ? `${r.city} - ${r.state}` : r.city || r.state || '';
  return [r.address, cityState, r.postalCode ? `CEP ${r.postalCode}` : '']
    .filter(Boolean)
    .join(' | ');
};

interface OrderSettingsConfigProps {
  emitterDisabled: boolean;
  onLoadingCartChange?: (loading: boolean) => void;
}

const ErrorMessage = ({ canShow }: { canShow: boolean }) => {
  if (!canShow) {
    return null;
  }

  const dispatch = useDispatch();
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCartState.emitterId
  ) as ICustomer;

  useEffect(() => {
    if (canShow) {
      dispatch(
        sendTagging({
          event: 'pedido_nao_encontrado',
          screenName: 'comprar_por_histórico',
          select_emissor: customer?.name ?? '',
          etapa: 'selecionar_emissor',
          event_category: 'erro',
          menssagem:
            'Nenhum pedido encontrado. Tente novamente com um emissor diferente.'
        })
      );
    }
  }, [canShow]);

  return (
    <HeaderErrorContainer>
      <HeaderErrorText>
        <Icon option="CloseCircle" variant="danger" size="sm" /> Nenhum pedido
        encontrado. Tente novamente com um emissor diferente.
      </HeaderErrorText>
    </HeaderErrorContainer>
  );
};

const OrderSettingsConfig: React.FC<OrderSettingsConfigProps> = ({
  emitterDisabled,
  onLoadingCartChange
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');
  const newEmitterReceiverSelect = GetFlag('new_emitter_receiver_select');
  const migrationFlag = GetFlag('purchase-migration-brdi-brin');
  const newSearchEmitterInputAdmin = GetFlag('new_search_emitter_input_admin');
  const filterEmittersFlag = GetFlag('filter_emitters');

  const { createNewCart, deactivateCart, warmup } = useCartService();
  const { deleteCartId } = useCart();

  const lastOrdersState = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch.lastOrdersState
  );

  const loadingOrder = useSelector(
    (state: ApplicationState) =>
      state.longSteelOrderItemSearch.lastOrdersState.loading
  );

  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const userState = useSelector((state: ApplicationState) => state.user);
  const purchaseCustomers = useSelector((state: ApplicationState) =>
    filterInvalidCustomers(
      isUserAdmin
        ? state.customers.customers
        : state.customers.customers.filter((customer) =>
            userState.roles.ordersInputCustomers.includes(customer.customerId)
          ),
      filterEmittersFlag
    )
  );

  const receiverLoading = useSelector(
    (state: ApplicationState) => state?.entryOrderReceivers?.loading || false
  );

  const receiverOptionsRedux = useSelector(
    (state: ApplicationState) => state?.entryOrderReceivers?.receivers || []
  );

  const receiverOptions = receiverOptionsRedux.map((receiver) => {
    return {
      label: receiver?.name,
      value: receiver?.customerId,
      descriptions: [
        `${uppercaseOnlyFirstLetter(
          receiver.address
        )} | ${uppercaseOnlyFirstLetter(receiver?.city)} - ${
          receiver.state
        } | CEP ${receiver?.postalCode}`,
        `N° do cliente ${receiver?.customerId}`,
        `CNPJ ${receiver?.taxNumber}`
      ]
    };
  });

  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCartState.emitterId
  ) as ICustomer;

  const descriptionResponse =
    newSearchItemsEndpointFlag || customer?.productsDescriptionResponse;

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById ||
    !!loadingOrder;

  const shouldResetOnMount = isUserAdmin && newSearchEmitterInputAdmin;

  const [emitterId, setEmitterId] = useState(
    shouldResetOnMount ? '' : shoppingCartState.emitterId
  );
  const [receiverId, setReceiverId] = useState(
    shouldResetOnMount ? '' : shoppingCartState.receiverId
  );
  const [adminBpBuffer, setAdminBpBuffer] = useState(
    shouldResetOnMount ? '' : shoppingCartState.emitterId
  );
  const [customerOrder] = useState(shoppingCartState.customerOrder);
  const [isTagged, setIsTagged] = useState(false);

  const [earlyPayment, setEarlyPayment] = useState(false);
  const [showModalError, setShowModalError] = useState(false);

  const [showModalChangeCustomer, setShowModalChangeCustomer] = useState(false);
  const [showBpUnavailableModal, setShowBpUnavailableModal] = useState(false);

  const [isEditingEmitter, setIsEditingEmitter] = useState(false);
  const [isEditingReceiver, setIsEditingReceiver] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const emitterSelectRef = useRef<HTMLDivElement>(null);
  const receiverSelectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldResetOnMount) return;
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: '',
        receiverId: ''
      })
    );
  }, []);

  const { selectEmitter } = useSelectEmitter();

  useEffect(() => {
    if (!isEditingEmitter || !emitterSelectRef.current) return;
    const timer = setTimeout(() => {
      const input = emitterSelectRef.current?.querySelector<HTMLElement>(
        '[data-testid="hefesto-select-input"]'
      );
      input?.click();
    }, 50);
    return () => clearTimeout(timer);
  }, [isEditingEmitter]);

  useEffect(() => {
    if (!isEditingReceiver || !receiverSelectRef.current) return;
    const timer = setTimeout(() => {
      const input = receiverSelectRef.current?.querySelector<HTMLElement>(
        '[data-testid="hefesto-select-input"]'
      );
      input?.click();
    }, 50);
    return () => clearTimeout(timer);
  }, [isEditingReceiver]);

  const customerRef = useRef<ICustomer | null>(null);
  const cgModalCheckRef = useRef<(() => void) | null>(null);
  const cgModalShownForRef = useRef<string>('');
  const onSelectEmitterKeyRef = useRef<string>('');
  const updateSettings = (
    selectedEmitterId: string,
    selectedReceiverId: string,
    selectedCustomerOrder: string
  ) => {
    if (
      selectedEmitterId !== emitterId ||
      selectedReceiverId !== receiverId ||
      selectedCustomerOrder !== customerOrder
    ) {
      dispatch(
        updateLongSteelShoppingCartSettings({
          ...shoppingCartState,
          emitterId: selectedEmitterId,
          receiverId: selectedReceiverId || selectedEmitterId,
          customerOrder: selectedCustomerOrder
        })
      );
    }
  };

  const searchCustomerById = (id?: string) => {
    const resolvedId = id ?? emitterId;
    cgModalShownForRef.current = '';
    onSelectEmitterKeyRef.current = '';
    setEmitterId(resolvedId);
    setReceiverId('');
    dispatch(loadCustomersSuccess([]));
    dispatch(loadCustomerFromId(resolvedId));
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: resolvedId,
        receiverId: ''
      })
    );
    dispatch(loadReceiversForBP(resolvedId));
  };

  const isAdminBpInputDisabled = (): boolean => {
    return (
      customersState.loading ||
      customersState.error ||
      emitterDisabled ||
      !!customersState.loadingProductDescription ||
      !!customersState.loadingPurchaseReceivers ||
      !pathname.endsWith(EOrderSettingsRoutes.CHOOSE)
    );
  };

  const getCustomer = (customerId: string): ICustomer => {
    return purchaseCustomers.find(
      (customer) => customer.customerId === customerId
    ) as ICustomer;
  };

  const { commerceScreenName = '' } = getTaggingInformationScreen(
    location.pathname
  );

  const dispatchEmitterReceiverTagging = (customer: ICustomer) => {
    const receiverName = customer?.purchaseReceivers?.find(
      (receiver) => receiver.customerId == receiverId
    )?.name;
    if (receiverName && !isTagged && !customerLoading) {
      setIsTagged(true);
      dispatch(
        sendTagging({
          event: 'select_emissor_recebedor',
          screenName: commerceScreenName,
          etapa: 'select_emissor_recebedor',
          type: 'ecommerce',
          select_emissor: customer.name,
          select_recebedor: receiverName
        })
      );
    }
  };

  function loadReceivers() {
    const resolvedCustomer = customerRef.current;
    if (!resolvedCustomer) return;
    if (newEmitterReceiverSelect) {
      dispatch(loadReceiversForBP(resolvedCustomer.customerId));
    } else {
      dispatch(loadReceiversFrom(resolvedCustomer));
    }
  }

  const updateLoadingCart = (loading: boolean) => {
    setIsLoadingCart(loading);
    onLoadingCartChange?.(loading);
  };

  const resetSelections = () => {
    setEmitterId('');
    setReceiverId('');
    setAdminBpBuffer('');
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: '',
        receiverId: ''
      })
    );
  };

  const loadCartForSelection = async (
    soldTo: string,
    shipTo: string,
    errorMessage: string
  ): Promise<boolean> => {
    try {
      await createNewCart(soldTo, shipTo, false, true, false, true);
      return true;
    } catch {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: errorMessage,
          durationInMS: 5000
        })
      );
      resetSelections();
      return false;
    }
  };

  const onSelectEmitter = async (
    customerId: string,
    shipToCust?: string,
    cartErrorMessage?: string
  ) => {
    if (migrationFlag) {
      const selectedCustomer = purchaseCustomers.find(
        (c) => c.customerId === customerId
      );
      if (selectedCustomer?.customerType === 'CG') {
        trackPurchaseAttempt(userState, customerId).catch(() => null);
        setShowBpUnavailableModal(true);
        return;
      }
    }

    const finalShip = shipToCust || customerId;
    const callKey = `${customerId}|${finalShip}`;
    if (onSelectEmitterKeyRef.current === callKey) return;
    onSelectEmitterKeyRef.current = callKey;

    try {
      if (cartErrorMessage) {
        updateLoadingCart(true);
        const isCartLoaded = await loadCartForSelection(
          customerId,
          finalShip,
          cartErrorMessage
        );
        if (!isCartLoaded) {
          return;
        }
      }

      setEarlyPayment(false);
      setEmitterId(customerId);
      setReceiverId(finalShip);
      if (customerId !== '') {
        setIsTagged(false);
      }

      await selectEmitter(customerId, finalShip, customerOrder);
      if (cartErrorMessage) {
        updateLoadingCart(false);
      }

      if (
        newEmitterReceiverSelect &&
        pathname.includes(EOrderSettingsRoutes.CHOOSE)
      ) {
        warmup({ emitterId: customerId }).catch((error) => {
          console.error('Error on warmup', error);
        });

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } finally {
      if (cartErrorMessage) {
        updateLoadingCart(false);
      }
      onSelectEmitterKeyRef.current = '';
    }
  };

  const GlobalStyle = createGlobalStyle`
  body.modal-visible .hefesto-dropdown.hefesto-dropdown--bottom .hefesto-select-dropdown-options {
    display: none !important;
  }
`;

  useEffect(() => {
    if (showModalChangeCustomer) {
      document.body.classList.add('modal-visible');
    } else {
      document.body.classList.remove('modal-visible');
    }
  }, [showModalChangeCustomer]);

  const handleChangeBPButton = () => {
    localStorage.removeItem('selectedProducts');
    setEmitterId('');
    setShowModalChangeCustomer(false);
    window.location.reload();
  };

  const handleSelectFocus = () => {
    const productsList = JSON.parse(
      localStorage.getItem('selectedProducts') || '[]'
    );

    const pathSearchItems = pathname.includes(
      EOrderSettingsRoutes.SEARCH_ITEMS
    );

    if (emitterId && productsList.length > 0 && pathSearchItems) {
      setShowModalChangeCustomer(true);
      isEmitterDisabled();
    }
  };

  const handleChangeBP = () => {
    return (
      <ChangeBPModal
        showChangeBPModal={showModalChangeCustomer && !receiverLoading}
        loading={customersState.loading || receiverLoading}
        handleChangeBPButton={handleChangeBPButton}
        handleCancelButton={() => setShowModalChangeCustomer(false)}
      />
    );
  };

  useEffect(() => {
    setShowModalChangeCustomer(receiverLoading);
  }, [receiverLoading]);

  const onSelectReceiver = async (customerId: string) => {
    onSelectEmitter(
      emitterId,
      customerId,
      'Não foi possível alterar o recebedor do pedido'
    );
  };

  const loadCustomerBlocksIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.blocks &&
      !customersState.loadingBlocks &&
      !customersState.errorBlocks
    ) {
      dispatch(loadCustomerBlocks(customer.customerId));
    }
  };

  const loadProductDescriptionIfNeed = (customer: ICustomer) => {
    const isCatalogPage = pathname.includes(EOrderSettingsRoutes.CATALOG);
    const isSearchPage = pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS);
    if (
      customer &&
      (isCatalogPage || isSearchPage) &&
      !descriptionResponse &&
      !customersState.loadingProductDescription &&
      !customersState.errorProductDescription &&
      !newSearchItemsEndpointFlag
    ) {
      dispatch(loadCustomersProductDescriptionV2(customer.customerId));
    }
  };

  const loadPurchaseReceiversIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.purchaseReceivers &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      dispatch(loadCustomerPurchaseReceivers(customer.customerId));
    }
  };

  const areCustomerFieldsFilled = (customer: ICustomer) =>
    !!(
      customer?.name &&
      customer?.locality.address &&
      customer?.locality.city &&
      customer?.customerId &&
      customer?.locality.state &&
      customer?.taxNumber &&
      customer?.locality.zipCode
    );

  useEffect(() => {
    // eslint-disable-next-line prefer-const
    let isMounted = true;

    const customer = getCustomer(emitterId);
    customerRef.current = customer;

    if (customer && !customersState.error && !customersState.loading) {
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
    }

    if (isMounted && customer) {
      loadReceivers();
    }

    return () => {
      isMounted = false;
    };
  }, [emitterId, customersState.error, customersState.loading]);

  useEffect(() => {
    if (
      !customersState.loading &&
      !customersState.error &&
      customerRef.current
    ) {
      dispatchEmitterReceiverTagging(customerRef.current);
    }
  }, [customersState.loading, customersState.error]);

  useEffect(() => {
    if (customersState.errorBlocks && emitterId) {
      setCustomerBlockError();
    }
  }, [customersState.errorBlocks, emitterId]);

  const changeToEarlyPayment = () => {
    setEarlyPayment(true);
  };

  const setCustomerBlockError = () => {
    setShowModalError(true);
    setEarlyPayment(false);
    setEmitterId('');
    setReceiverId('');
    updateSettings('', '', customerOrder);
    dispatch(
      addToast({
        type: EToastTypes.DANGER,
        message:
          'Não foi possível validar os dados necessários para o emissor escolhido. Tente novamente mais tarde.',
        durationInMS: 5000
      })
    );
  };

  // useEffect(() => {
  //   const customer = getCustomer(emitterId);
  //   if (
  //     customer &&
  //     receiverId === '' &&
  //     !customersState.loadingPurchaseReceivers &&
  //     !customersState.errorPurchaseReceivers
  //   ) {
  //     // inicia receiverId se só retornar 1 na lista (purchaseReceivers)
  //     // if (customer.purchaseReceivers?.length === 1) {
  //     //   onSelectReceiver(customer.purchaseReceivers[0].code);
  //     // }
  //   }
  // });

  useEffect(() => {
    if (
      !getCustomer(emitterId) &&
      purchaseCustomers &&
      purchaseCustomers.length !== 0 &&
      !customersState.loadingCustomerById &&
      !customersState.errorBp &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      if (
        purchaseCustomers.length === 1 ||
        (isUserAdmin && purchaseCustomers.length === 2)
      ) {
        const onlyCustomer = purchaseCustomers[0];
        if (migrationFlag && onlyCustomer.customerType === 'CG') {
          if (cgModalShownForRef.current !== onlyCustomer.customerId) {
            cgModalShownForRef.current = onlyCustomer.customerId;
            trackPurchaseAttempt(userState, onlyCustomer.customerId).catch(
              () => null
            );
            setShowBpUnavailableModal(true);
          }
          return;
        }
        updateSettings(onlyCustomer.customerId, '', customerOrder);
        onSelectEmitter(onlyCustomer.customerId as string);
      }
    }
  }, [
    emitterId,
    isUserAdmin,
    purchaseCustomers,
    customersState.loadingCustomerById,
    customersState.errorBp,
    customersState.loadingPurchaseReceivers,
    customersState.errorPurchaseReceivers
  ]);

  useEffect(() => {
    if (
      isUserAdmin &&
      !receiverLoading &&
      receiverOptionsRedux.length === 1 &&
      !receiverId &&
      emitterId
    ) {
      const cgCustomer = getCustomer(emitterId);
      if (cgCustomer?.customerType === 'CG') return;
      onSelectEmitter(emitterId, receiverOptionsRedux[0].customerId);
    }
  }, [
    receiverLoading,
    receiverOptionsRedux,
    isUserAdmin,
    receiverId,
    emitterId
  ]);

  cgModalCheckRef.current = () => {
    if (
      isUserAdmin &&
      !customersState.loadingCustomerById &&
      !customersState.errorBp &&
      !customersState.loadingBlocks &&
      !receiverLoading &&
      emitterId &&
      migrationFlag &&
      cgModalShownForRef.current !== emitterId
    ) {
      const cgCustomer = getCustomer(emitterId);
      if (cgCustomer?.customerType === 'CG') {
        cgModalShownForRef.current = emitterId;
        trackPurchaseAttempt(userState, emitterId).catch(() => null);
        setShowBpUnavailableModal(true);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => cgModalCheckRef.current?.(), 0);
    return () => clearTimeout(timer);
  }, [
    isUserAdmin,
    customersState.loadingCustomerById,
    customersState.errorBp,
    customersState.loadingBlocks,
    receiverLoading,
    emitterId,
    purchaseCustomers
  ]);

  const isEmitterDisabled = (): boolean => {
    return (
      isLoadingCart ||
      customersState.loading ||
      customersState.error ||
      emitterDisabled ||
      (!isUserAdmin &&
        purchaseCustomers.length === 1 &&
        emitterId.length !== 0) ||
      !!customersState.loadingProductDescription ||
      !!customersState.loadingPurchaseReceivers ||
      !pathname.endsWith(EOrderSettingsRoutes.CHOOSE)
    );
  };

  const isReceiverDisabled = (): boolean => {
    return (
      !emitterId ||
      customersState.loading ||
      customersState.error ||
      !!customersState.loadingPurchaseReceivers ||
      (!isUserAdmin && purchaseCustomers.length === 1) ||
      !pathname.endsWith(EOrderSettingsRoutes.CHOOSE)
    );
  };
  const emitterOptions = purchaseCustomers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `N° do cliente ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const getCustomerIdByEmitterId = () => {
    return purchaseCustomers
      .filter((customer) => customer.customerId === emitterId)
      .pop()?.customerId;
  };

  return (
    <>
      <Box>
        <Container>
          {isUserAdmin ? (
            newSearchEmitterInputAdmin ? (
              <AdminSearchRow>
                <AdminBpInput
                  title="Emissor do pedido"
                  customerId={emitterId}
                  setCustomerId={setEmitterId}
                  onSearch={(id) => searchCustomerById(id)}
                  onChange={setAdminBpBuffer}
                  loading={customersState.loading}
                  bpError={
                    !customersState.loading && (customersState.errorBp ?? false)
                  }
                  customerName={uppercaseOnlyFirstLetter(
                    getCustomer(emitterId)?.name
                  )}
                  disabled={isAdminBpInputDisabled()}
                />
                <HefestoButton
                  name="btn-buscar-emissor-compra"
                  variant="primary"
                  disabled={!adminBpBuffer || customersState.loading}
                  onClick={() => searchCustomerById(adminBpBuffer)}
                  icon={<Icon option="Search" size="xs" />}
                >
                  Buscar
                </HefestoButton>
              </AdminSearchRow>
            ) : (
              <FilterInputContainer>
                <h4>Emissor do pedido</h4>
                <Form>
                  <Form.Item
                    hasFeedback={customersState.loading}
                    validateStatus={
                      !customersState.loading && customersState.errorBp
                        ? 'error'
                        : ''
                    }
                    help={
                      customersState.errorBp && !customersState.loading
                        ? 'BP não encontrado'
                        : uppercaseOnlyFirstLetter(getCustomer(emitterId)?.name)
                    }
                  >
                    <Input.Search
                      loading={customersState.loading}
                      disabled={isEmitterDisabled()}
                      placeholder="Digite o BP"
                      value={emitterId}
                      onChange={(e) => setEmitterId(e.target.value)}
                      onSearch={searchCustomerById}
                    />
                  </Form.Item>
                </Form>
              </FilterInputContainer>
            )
          ) : (
            <>
              <GlobalStyle />
              <EmitterSelectContainer>
                {!isEditingEmitter && !showModalChangeCustomer ? (
                  <BpRichDisplay
                    fieldLabel="Emissor do pedido"
                    name={
                      emitterId && getCustomer(emitterId)
                        ? getCustomer(emitterId).name
                        : undefined
                    }
                    address={
                      emitterId && getCustomer(emitterId)
                        ? buildBpAddressString(getCustomer(emitterId).locality)
                        : undefined
                    }
                    placeholder="Selecione um emissor do pedido"
                    disabled={isEmitterDisabled()}
                    onClick={() => {
                      setIsEditingEmitter(true);
                      handleSelectFocus();
                    }}
                  />
                ) : (
                  <div ref={emitterSelectRef}>
                    <Select
                      label="Emissor do pedido"
                      name="select-emissor-pedido"
                      options={emitterOptions}
                      value={getCustomerIdByEmitterId()}
                      onSelect={(customerId) => {
                        if (!showModalChangeCustomer) {
                          onSelectEmitter(
                            customerId as string,
                            undefined,
                            'Não foi possível alterar o emissor do pedido'
                          );
                        }
                        setIsEditingEmitter(false);
                      }}
                      isMultipleSelect={false}
                      placeholder="Selecione um emissor do pedido"
                      disabled={isEmitterDisabled() || showModalChangeCustomer}
                      onFocus={handleSelectFocus}
                      onBlur={() => {
                        setTimeout(() => setIsEditingEmitter(false), 150);
                      }}
                    />
                  </div>
                )}
              </EmitterSelectContainer>
            </>
          )}
          <EmitterSelectContainer>
            {!isEditingReceiver && !isUserAdmin ? (
              (() => {
                const sel = receiverOptionsRedux.find(
                  (r) => r.customerId === receiverId
                );
                return (
                  <BpRichDisplay
                    fieldLabel="Recebedor do pedido"
                    name={sel?.name}
                    address={
                      sel ? buildBpAddressStringFromReceiver(sel) : undefined
                    }
                    placeholder="Selecione um recebedor do pedido"
                    disabled={isReceiverDisabled() || isEmitterDisabled()}
                    onClick={() => setIsEditingReceiver(true)}
                  />
                );
              })()
            ) : (
              <div ref={receiverSelectRef}>
                <Select
                  label="Recebedor do pedido"
                  placeholder="Selecione um recebedor do pedido"
                  options={receiverOptions || []}
                  name="select-receiver"
                  disabled={isReceiverDisabled() || isEmitterDisabled()}
                  onSelect={(receiver) => {
                    onSelectReceiver(receiver as string);
                    setIsEditingReceiver(false);
                  }}
                  value={receiverId}
                  onBlur={() => {
                    setTimeout(() => setIsEditingReceiver(false), 150);
                  }}
                />
              </div>
            )}
          </EmitterSelectContainer>

          {purchaseCustomers.length > 0 && emitterId && (
            <Modal
              visible={
                !!getCustomer(emitterId)?.blocks?.length &&
                !earlyPayment &&
                !customersState.loadingProductDescription &&
                showModalError
              }
              closable={false}
              width={600}
              title={'Encontramos um problema'}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => {
                    // onSelectEmitter('');
                    // setEmitterId('');
                    setShowModalError(false);
                  }}
                >
                  Cancelar
                </Button>,
                <Button
                  key="earlyPayment"
                  type="primary"
                  onClick={() => changeToEarlyPayment()}
                >
                  Pagamento antecipado
                </Button>
              ]}
            >
              {!!getCustomer(emitterId)?.blocks?.length &&
                !earlyPayment &&
                !customersState.loadingProductDescription && (
                  <CustomerBlockModalContent
                    customer={getCustomer(emitterId)}
                  />
                )}
            </Modal>
          )}
        </Container>

        <ErrorMessage
          canShow={
            !customerLoading &&
            !!shoppingCartState.emitterId &&
            lastOrdersState.lastOrders.length === 0 &&
            location.pathname.includes(EOrderSettingsRoutes.REPEAT_ORDER)
          }
        />
        <Container>
          <StepByStepComponent />
          {handleChangeBP()}
        </Container>
      </Box>
      {showBpUnavailableModal && (
        <BpUnavailableModal
          onCloseModal={() => {
            setShowBpUnavailableModal(false);
            resetSelections();
          }}
        />
      )}
    </>
  );
};

export default OrderSettingsConfig;
